/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import EmployeeNameSearch from '../../../../components/EmployeeNameSearch';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';

// =============================================================================
// Painel 2 — Dados do serviço.
//
// Duas seções lado a lado dentro do bloco:
//   • ESQUERDA (somente leitura): técnico atribuído, serviços previstos e tempo
//     estimado. Definidos pela camada de roteirização — o fechamento só
//     consome; a fonte real dos serviços prestados são as comissões.
//   • DIREITA (editável): dados da origem (vendedor + departamento) e pagamento
//     (cobrança), mais observações. É a ÚNICA parte editável do item, e por
//     isso carrega o botão Editar/Cancelar/Salvar da ficha.
//
// Componente CONTROLADO: estado de edição (formData, isEditing, isSaving,
// fieldErrors) vive em RoutingItemShow; o ciclo Editar/Cancelar/Salvar é
// disparado pelos callbacks recebidos por prop. Aqui só mantemos o search text
// do autocomplete de vendedor — UI puro que não vai pra payload.
//
// Whitelist editável (vendedor, departamento, cobrança, observações) casa com
// ScheduleItemService.update#allowedFields no backend.
// =============================================================================

// Vendedor remove is_salesperson porque o cadastro tem inconsistências
// (vendedores reais sem a flag marcada); confiamos só no employment_status_not
// pra excluir desligados. employment_status_not aceita CSV; usamos só
// 'DESLIGADO' pra incluir ativos, em experiência, afastados e em férias.
const SELLER_SEARCH_PARAMS = { employment_status_not: 'DESLIGADO' };

const PAYMENT_COLLECTION_OPTIONS = [
   { value: '', label: '—' },
   { value: 'ANTECIP_OU_NO_ATO', label: 'Antecipado ou no ato' },
   { value: 'A_PRAZO', label: 'A prazo' },
   { value: 'ISENTO', label: 'Isento' },
   { value: 'OUTROS', label: 'Outros' },
   { value: 'PENDENTE', label: 'Pendente' },
];

// Duas seções viram containers independentes: cada uma é um card próprio, e o
// fundo (escuro) da página aparece no espaço entre eles — separação nítida.
const SectionGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
   align-items: stretch;
   margin-bottom: 12px;

   @media (min-width: 768px) {
      gap: 16px;
      margin-bottom: 16px;
   }
   @media (max-width: 900px) {
      grid-template-columns: 1fr;
   }
`;

// Card = mesmo visual do Section, mas em coluna flex (pro rodapé grudar embaixo)
// e sem margin-bottom (o gap do grid cuida do espaçamento).
const Card = styled(Section)`
   margin-bottom: 0;
   display: flex;
   flex-direction: column;
   height: 100%;
`;

// Rodapé do card editável: empurra o botão pro canto inferior direito.
const CardFooter = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: auto;
   padding-top: 14px;
`;

const Stack = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
`;

const SubBlock = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const SubTitle = styled.h3`
   font-size: 0.72rem;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   margin: 0;
`;

const ReadOnlyValue = styled.div`
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   padding: 6px 0;
   min-height: 32px;
`;

const FieldError = styled.div`
   font-size: 0.7rem;
   color: #c62828;
   margin-top: 4px;
`;

const ServiceChips = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;

   .chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 9px;
      border-radius: 999px;
      background: #f5f5f3;
      border: 1px solid #e2e2dc;
      font-size: 0.78rem;
      color: #14140f;
   }
   .qty {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-weight: 600;
      color: #8e2828;
   }
`;

// Nota informativa: técnico e serviços são definidos na roteirização, não aqui.
const InfoHint = styled.div`
   font-size: 0.7rem;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#92400e'};
   margin-top: 4px;
   line-height: 1.4;
`;

const OperationLabel = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-size: 0.8rem;
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

   input {
      margin: 0;
   }
`;

function formatDuration(minutes) {
   if (minutes === null || minutes === undefined || minutes === '') return '—';
   const numeric = Number(minutes);
   if (Number.isNaN(numeric)) return '—';
   return `${numeric} min`;
}

// =============================================================================

export default function ItemEditFormPanel({
   item,
   departments,
   formData,
   onFieldChange,
   isEditing,
   isSaving,
   fieldErrors,
   isRouted = false,
   canEdit = false,
   onStartEditing,
   onCancelEditing,
   onSave,
}) {
   // Search text do autocomplete de vendedor é UI local — não vira payload.
   const [sellerSearchText, setSellerSearchText] = useState(
      item?.seller?.full_name ?? ''
   );

   // Resync com o item: ao carregar, ao salvar (refetch) e ao cancelar edição
   // (transição isEditing true → false).
   useEffect(() => {
      setSellerSearchText(item?.seller?.full_name ?? '');
   }, [item, isEditing]);

   if (!item || !formData) return null;

   const isReadOnly = !isEditing || isSaving;

   // Mutual exclusion vendedor × vendedor externo. A UI desabilita
   // reciprocamente; o submit também valida (defesa em profundidade).
   const hasSellerSelected = formData.seller_employee_id != null;
   const isExternalSeller = formData.is_salesperson_external === 1;

   // Texto read-only do técnico (sem ternário aninhado no JSX).
   let technicianDisplay = item.assignedTechnician?.full_name ?? null;
   if (!technicianDisplay) {
      if (item.is_freelance_technician === 1) technicianDisplay = 'Freelance';
      else if (isRouted) technicianDisplay = '—';
      else technicianDisplay = 'A definir na roteirização';
   }

   return (
      <SectionGrid>
         {/* ══ CARD 1 — somente leitura: técnico + serviços ══ */}
         <Card>
            <SectionHeader>
               <SectionTitle>Técnico e serviços</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <Stack>
                  {/* Técnico (somente leitura). A atribuição pertence à camada
                      de roteirização (board: designar técnico → atribuir rota),
                      não ao cadastro/edição do agendamento. */}
                  <Field $col={12} $colMobile={12}>
                     <label>Técnico atribuído</label>
                     <ReadOnlyValue>{technicianDisplay}</ReadOnlyValue>
                     <InfoHint>
                        O técnico é definido pela atribuição da rota, no quadro
                        de roteirização — não nesta etapa.
                     </InfoHint>
                  </Field>

                  {/* Serviços previstos (somente leitura). Estipulados pela
                      roteirização; o fechamento apenas reaproveita a info para
                      popular comissões — a fonte real dos serviços prestados
                      são as comissões. */}
                  <Field $col={12} $colMobile={12}>
                     <label>Serviços previstos</label>
                     <ReadOnlyValue>
                        {item.services && item.services.length > 0 ? (
                           <ServiceChips>
                              {item.services.map((s) => (
                                 <span
                                    className="chip"
                                    key={s.commission_table_id}
                                 >
                                    {s.service_description}
                                    <span className="qty">×{s.quantity}</span>
                                 </span>
                              ))}
                           </ServiceChips>
                        ) : (
                           '—'
                        )}
                     </ReadOnlyValue>
                     <InfoHint>
                        Definidos na roteirização. Os serviços efetivamente
                        prestados são registrados nas comissões.
                     </InfoHint>
                  </Field>

                  {/* Tempo estimado (somente leitura, derivado do catálogo). */}
                  <Field $col={12} $colMobile={12}>
                     <label>Tempo estimado (catálogo)</label>
                     <ReadOnlyValue>
                        {formatDuration(item.estimated_duration_minutes)}
                     </ReadOnlyValue>
                  </Field>

                  {/* Observações (somente leitura). Registro da logística/
                      vendedor no cadastro — o fechamento não altera. */}
                  <Field $col={12} $colMobile={12}>
                     <label>Observações</label>
                     <ReadOnlyValue style={{ whiteSpace: 'pre-wrap' }}>
                        {item.notes ?? '—'}
                     </ReadOnlyValue>
                  </Field>
               </Stack>
            </SectionBody>
         </Card>

         {/* ══ CARD 2 — editável: origem + pagamento + observações ══ */}
         <Card>
            <SectionHeader>
               <SectionTitle>Origem e pagamento</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <Stack>
                  {/* ── Subseção: dados da origem ── */}
                  <SubBlock>
                     <SubTitle>Dados da origem</SubTitle>
                     <FormGrid $cols={12} $colsMobile={12}>
                        {/* Vendedor */}
                        <Field $col={7} $colMobile={12}>
                           <label>Vendedor</label>
                           {isReadOnly ? (
                              <ReadOnlyValue>
                                 {item.seller?.full_name ??
                                    (item.is_salesperson_external === 1
                                       ? 'Vendedor externo'
                                       : '—')}
                              </ReadOnlyValue>
                           ) : (
                              <EmployeeNameSearch
                                 value={sellerSearchText}
                                 onChange={setSellerSearchText}
                                 onSelect={(employee) => {
                                    onFieldChange(
                                       'seller_employee_id',
                                       employee?.id ?? null
                                    );
                                    setSellerSearchText(
                                       employee?.full_name ?? ''
                                    );
                                    if (employee) {
                                       onFieldChange(
                                          'is_salesperson_external',
                                          0
                                       );
                                    }
                                 }}
                                 initialSelected={item.seller ?? null}
                                 extraParams={SELLER_SEARCH_PARAMS}
                                 placeholder="Digite o nome do vendedor..."
                                 disabled={isSaving || isExternalSeller}
                              />
                           )}
                           {fieldErrors.seller_employee_id && (
                              <FieldError>
                                 {fieldErrors.seller_employee_id}
                              </FieldError>
                           )}
                        </Field>

                        {/* Departamento — ao lado do vendedor (origem da venda
                            é vinculada ao vendedor). */}
                        <Field $col={5} $colMobile={12}>
                           <label>Departamento</label>
                           {isReadOnly ? (
                              <ReadOnlyValue>
                                 {item.department?.department_name ?? '—'}
                              </ReadOnlyValue>
                           ) : (
                              <select
                                 value={formData.department_id ?? ''}
                                 onChange={(e) =>
                                    onFieldChange(
                                       'department_id',
                                       e.target.value
                                    )
                                 }
                                 disabled={isSaving}
                              >
                                 <option value="">—</option>
                                 {departments.map((dept) => (
                                    <option key={dept.id} value={dept.id}>
                                       {dept.department_name}
                                    </option>
                                 ))}
                              </select>
                           )}
                           {fieldErrors.department_id && (
                              <FieldError>
                                 {fieldErrors.department_id}
                              </FieldError>
                           )}
                        </Field>

                        {/* Vendedor externo */}
                        <Field $col={12} $colMobile={12}>
                           <OperationLabel
                              $disabled={isReadOnly || hasSellerSelected}
                           >
                              <input
                                 type="checkbox"
                                 checked={
                                    formData.is_salesperson_external === 1
                                 }
                                 onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    onFieldChange(
                                       'is_salesperson_external',
                                       isChecked ? 1 : 0
                                    );
                                    if (isChecked) {
                                       onFieldChange(
                                          'seller_employee_id',
                                          null
                                       );
                                       setSellerSearchText('');
                                    }
                                 }}
                                 disabled={isReadOnly || hasSellerSelected}
                              />
                              Vendedor externo
                           </OperationLabel>
                        </Field>
                     </FormGrid>
                  </SubBlock>

                  {/* ── Subseção: pagamento ── */}
                  <SubBlock>
                     <SubTitle>Pagamento</SubTitle>
                     <FormGrid $cols={12} $colsMobile={12}>
                        <Field $col={7} $colMobile={12}>
                           <label>Cobrança</label>
                           {isReadOnly ? (
                              <ReadOnlyValue>
                                 {PAYMENT_COLLECTION_OPTIONS.find(
                                    (opt) =>
                                       opt.value ===
                                       item.payment_collection_type
                                 )?.label ?? '—'}
                              </ReadOnlyValue>
                           ) : (
                              <select
                                 value={formData.payment_collection_type ?? ''}
                                 onChange={(e) =>
                                    onFieldChange(
                                       'payment_collection_type',
                                       e.target.value
                                    )
                                 }
                                 disabled={isSaving}
                              >
                                 {PAYMENT_COLLECTION_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                       {opt.label}
                                    </option>
                                 ))}
                              </select>
                           )}
                        </Field>
                     </FormGrid>
                  </SubBlock>
               </Stack>
            </SectionBody>

            {/* Botão Editar/Cancelar/Salvar no canto inferior do card editável. */}
            <CardFooter>
               {canEdit && !isEditing && (
                  <button type="button" onClick={onStartEditing}>
                     Editar
                  </button>
               )}
               {isEditing && (
                  <>
                     <button
                        type="button"
                        onClick={onCancelEditing}
                        disabled={isSaving}
                     >
                        Cancelar
                     </button>
                     <button type="button" onClick={onSave} disabled={isSaving}>
                        {isSaving ? 'Salvando…' : 'Salvar'}
                     </button>
                  </>
               )}
            </CardFooter>
         </Card>
      </SectionGrid>
   );
}

ItemEditFormPanel.propTypes = {
   item: PropTypes.object,
   departments: PropTypes.arrayOf(PropTypes.object),
   formData: PropTypes.object,
   onFieldChange: PropTypes.func.isRequired,
   isEditing: PropTypes.bool,
   isSaving: PropTypes.bool,
   fieldErrors: PropTypes.object,
   isRouted: PropTypes.bool,
   canEdit: PropTypes.bool,
   onStartEditing: PropTypes.func,
   onCancelEditing: PropTypes.func,
   onSave: PropTypes.func,
};
